import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  SelectChangeEvent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import userService, { User, Property, CreateUserData, UpdateUserData } from '../../services/userService';

const UserList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<'admin' | 'unit_manager'>('unit_manager');
  const [selectedProperties, setSelectedProperties] = useState<number[]>([]);
  
  useEffect(() => {
    fetchUsers();
    fetchProperties();
  }, []);
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getUsers();
      setUsers(data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchProperties = async () => {
    try {
      const data = await userService.getProperties();
      setProperties(data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };
  
  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setUsername(user.username);
      setEmail(user.email || '');
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setRole(user.profile?.role || 'unit_manager');
      setSelectedProperties(user.profile?.properties_detail?.map(p => p.id) || []);
      setPassword(''); // Don't show password
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };
  
  const resetForm = () => {
    setEditingUser(null);
    setUsername('');
    setPassword('');
    setEmail('');
    setFirstName('');
    setLastName('');
    setRole('unit_manager');
    setSelectedProperties([]);
  };
  
  const handleSave = async () => {
    // Validation
    if (!editingUser && !username) {
      alert('Username is required');
      return;
    }
    if (!editingUser && !password) {
      alert('Password is required for new users');
      return;
    }
    
    try {
      setSaving(true);
      if (editingUser) {
        // Update user
        const updateData: UpdateUserData = {
          email,
          first_name: firstName,
          last_name: lastName,
          role,
          property_ids: selectedProperties,
          is_active: true
        };
        if (password) {
          updateData.password = password;
        }
        await userService.updateUser(editingUser.id, updateData);
      } else {
        // Create user
        const createData: CreateUserData = {
          username,
          password,
          email,
          first_name: firstName,
          last_name: lastName,
          role,
          property_ids: selectedProperties
        };
        await userService.createUser(createData);
      }
      setDialogOpen(false);
      fetchUsers();
      resetForm();
    } catch (err: any) {
      console.error('Error saving user:', err);
      alert(err.response?.data?.message || 'Error saving user. Please check the console.');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async (userId: number) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await userService.deleteUser(userId);
        fetchUsers();
      } catch (err: any) {
        console.error('Error deleting user:', err);
        alert(err.response?.data?.message || 'Error deleting user');
      }
    }
  };
  
  const handlePropertyChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value;
    setSelectedProperties(typeof value === 'string' ? [] : value);
  };
  
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create User
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Properties</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No users found. Create one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{`${user.first_name} ${user.last_name}`.trim() || '-'}</TableCell>
                    <TableCell>{user.email || '-'}</TableCell>
                    <TableCell>
                      {user.profile ? (
                        <Chip
                          label={user.profile.role === 'admin' ? 'Admin' : 'Unit Manager'}
                          color={user.profile.role === 'admin' ? 'primary' : 'default'}
                          size="small"
                        />
                      ) : (
                        <Chip label="No Profile" color="warning" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {!user.profile || user.profile.properties_detail.length === 0 
                        ? '-' 
                        : user.profile.properties_detail.map(p => p.name).join(', ')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? 'Active' : 'Inactive'}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        onClick={() => handleOpenDialog(user)} 
                        size="small"
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDelete(user.id)} 
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Create New User'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Username"
            fullWidth
            margin="normal"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={!!editingUser}
            required
            helperText={editingUser ? 'Username cannot be changed' : 'Required'}
          />
          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            helperText={editingUser ? 'Leave blank to keep current password' : 'Required - minimum 6 characters'}
            required={!editingUser}
          />
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            label="First Name"
            fullWidth
            margin="normal"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <TextField
            label="Last Name"
            fullWidth
            margin="normal"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Role</InputLabel>
            <Select value={role} onChange={(e) => setRole(e.target.value as any)} label="Role">
              <MenuItem value="unit_manager">Unit Manager</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Assigned Properties</InputLabel>
            <Select
              multiple
              value={selectedProperties}
              onChange={handlePropertyChange}
              label="Assigned Properties"
              renderValue={(selected) => 
                properties
                  .filter(p => selected.includes(p.id))
                  .map(p => p.name)
                  .join(', ') || 'None'
              }
            >
              {properties.map((property) => (
                <MenuItem key={property.id} value={property.id}>
                  <Checkbox checked={selectedProperties.includes(property.id)} />
                  <ListItemText primary={property.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : (editingUser ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserList;

